// import {parse_glb} from './glb';
import {each, assert, uniform1, DEG2RAD, range, choice, chance, expovariate, uniform, lerp, clamp, smoothstep} from './utils';
import {mat3, mat4, quat, vec3, vec2, vec4} from 'gl-matrix';
import {use_program, load_texture, create_texture} from './shader-utils';
import {create_buffer} from './webgl';
import _random_color from 'randomcolor';
import {Camera} from './camera';
import {Orbit} from './orbit';
import {add_hotkeys} from './hotkeys';
import {PickRay} from './pick-ray';
import {simplex2} from './noise';
import msgpack from './msgpack';
import {PostProc} from './postproc';

const make_random_color = () => new Float32Array(_random_color({format: 'rgbArray'}).map(x => x/255));
const fetch_bin = url => fetch(url).then(r => r.arrayBuffer());
const PI2 = 2 * Math.PI;

export function init_sculpture(env) {
    env.params.add_float('ambient_light', {min:0, max: 2, default: 0.623});
    env.params.add_float('key_light', {min:0, max: 2, default: 0.628});
    env.params.add_float('projection_light', {min:0, max: 2, default: 1.301});
    env.params.add_float('projection_fov', {min:1, max: 20, default: 10});
    env.params.add_enum('projection_mode', {options: ['clip', 'repeat']});

    env.params.add_float('roughness', {min:0, max: 1, default: 0.154});
    env.params.add_float('reflectance', {min:0, max: 1, default: 0.514});

    env.params.add_float('dof_focus', {min:0, max: 20, default: 7.8});
    env.params.add_float('dof_near', {min:0, max: 10, default: 5});
    env.params.add_float('dof_far', {min:0, max: 10, default: 1});
    env.params.add_float('dof_clamp_far', {min:0, max: 1, default: 0.8});
    env.params.add_float('dof_max_coc', {min:0, max: 100, default: 10/2});
    env.params.add_float('dof_radius_scale', {min:0, max: 1, default: 0.4/2});

    env.params.add_bool('dof_visualize', {default: false});
    env.params.add_bool('dof_enable', {default: true});
    env.params.add_bool('fxaa_enable', {default: true});

    const postproc = new PostProc;

    let models = null;

    let exploding = false;
    let explode_amount = 0.0;
    let explode_start = 0;
    const slogan_color = vec4.create();
    let shaking = false;
    let shake_start_time = 0;
    let shake_sound_played = false;
    const mat_shake = mat4.create();

    let dolly = 0;
    let dolly_delta = 0;
    let slogan_alpha = 0;
    let emission_factor = 0;

    const tex_slogan = load_texture({
        url: 'images/slogan2.png',
        flip: true,
    });
    const buf_quad = create_buffer(gl.ARRAY_BUFFER, [0,0, 1,0, 0,1, 1,1]);

    fetch_bin('models/models.bin')
        .then(ab => msgpack.decode(new Uint8Array(ab)))
        .then(ob => {
            models = Object.assign(ob, {
                buffers: {
                    vertex: create_buffer(gl.ARRAY_BUFFER, ob.data.vertex),
                    element: create_buffer(gl.ELEMENT_ARRAY_BUFFER, ob.data.element),
                },
            });

            ['gem_010', 'gem_010b'].forEach(name => {
                const model = models.models.find(m => m.name == name);
                assert(model);
                model.tex_occlusion = load_texture({
                    url: `models/${name}_ao.jpg`,
                    flip: false,
                });
            });

            each(models.parts, part => {
                part.axis = vec3.random(vec3.create(), 1.0);
                part.spin = uniform1() * DEG2RAD * 100;
                part.angle = 0;
                part.rot = mat4.create();
            });
        });

    // temps
    const mat = mat4.create();
    const mat_normal = mat3.create();
    const axis = vec3.create();
    const mat_parent = mat4.create();   // for rotation
    const v0 = vec3.create();
    const v1 = vec3.create();
    const v2 = vec3.create();

    // object rotation
    const rot = quat.create();
    const spin = quat.create();
    const wobble = quat.create();

    let projector_mode = 0;

    function Projector(idx) {
        this.camera = new Camera;
        this.orbit = new Orbit;
        this.color = make_random_color();
        this.seqnum = 0;
    }

    Projector.prototype.randomize = function() {
        const {orbit, camera} = this;
        orbit.rotate[0] = uniform1() * Math.PI;
        orbit.rotate[1] = uniform1() * Math.PI;

        const q = 1e-3;
        orbit.spin[0] = q * uniform1();
        orbit.spin[1] = q * uniform1();

        orbit.update();
        orbit.get_view(camera.view);

        // camera.viewport[2] = 10;
        // camera.viewport[3] = 1;

        //camera.update();
    };

    Projector.prototype.update = function() {
        //const {orbit, camera} = this;
        //vec3.add(orbit.rotate, orbit.rotate, orbit.spin);
        //orbit.update();
        //orbit.get_view(camera.view);
    };

    const projectors = range(3).map(i => {
        const proj = new Projector(i);
        const {orbit, camera} = proj;

        camera.name = 'proj' + i;
        camera.near = 2.0;
        camera.far = 30;
        camera.fov = 10 * DEG2RAD;
        camera.viewport = [0, 0, 1024, 1024];
        camera.ortho = false;
        camera.ortho_scale = 3;

        orbit.distance = 15;
        orbit.rotate[0] = uniform1() * Math.PI;
        orbit.rotate[1] = uniform1() * Math.PI;

        orbit.spin = vec3.create();

        orbit.update();
        orbit.get_view(camera.view);
        camera.update();
        return proj;
    });

    const projector_mats = new Float32Array(projectors.length * 16);
    const projector_dirs = new Float32Array(projectors.length * 3);
    const projector_intensities = new Float32Array(projectors.length * 1);

    function update_projector_params() {
        const fov = env.params.get_value('projection_fov');

        projectors.forEach((proj, idx) => {
            // update projection matrix to match slot aspect
            const slot = env.gallery.slots[idx];
            proj.camera.viewport[2] = 1024 * slot.aspect;
            proj.camera.viewport[3] = 1024;
            proj.camera.fov = DEG2RAD * fov;
            proj.camera.update();

            // copy matrices to uniforms
            projector_mats.set(proj.camera.mvp, 16*idx);
            projector_dirs.set(proj.camera.view_dir, 3*idx);

            // intensity
            projector_intensities[idx] = proj.intensity;
        });
    }
    update_projector_params();

    let main_camera = null;
    let main_orbit = null;

    function set_camera_to_projector(idx) {
        console.log('set_camera_to_projector:', idx);
        const proj = projectors[idx];

        if (env.camera === proj.camera) {
            env.camera = main_camera;
            env.orbit = main_orbit;
        } else {
            if (!main_camera) {
                // stash the main camera
                main_camera = env.camera;
                main_orbit = env.orbit;
            }

            env.camera = proj.camera;
            env.orbit = proj.orbit;
        }
    }

    function start_shaking() {
        if (!shaking) {
            shaking = true;
            shake_start_time = env.time;
            shake_sound_played = false;

            env.api_state_change({hasClickedAndHeld: true});
        }
    }

    function stop_shaking() {
        shaking = false;
    }

    // document.addEventListener('keydown', e => {
    //     if (!exploding && (e.code == 'Enter')) {
    //         start_shaking();
    //         e.preventDefault();
    //     }
    // });
    //
    // document.addEventListener('keyup', e => {
    //     if (shaking && (e.code == 'Enter')) {
    //         stop_shaking();
    //         e.preventDefault();
    //     }
    // });
    //
    function randomize_spin() {
        for (let i = 0; i < 3; ++i)
            axis[i] = uniform1();
        vec3.normalize(axis, axis);

        const q = 0.1;
        //const angle = q * uniform1() * Math.PI;
        const angle = q;
        quat.setAxisAngle(spin, axis, angle);
    }

    // randomize_spin();

    const fs_buf = create_buffer(gl.ARRAY_BUFFER, [-1, -1, 3, -1, -1, 3]);

    const mean_projector_randomize_interval = 2e3;
    let next_projector_randomize_time = 0;

    const mean_shimmer_interval = 5e3;
    let next_shimmer_time = expovariate(mean_shimmer_interval);
    let shimmer_params = vec4.create();

    const gradient_worlds = [
        // 2020-06-16
        ['d5d6db ffffff'],
        ['e0dfdd ece0d2'],
        ['ded8da ededef'],

        //['ecdfc6 d4bec9', 'd4bec9 e5e5e5'],
        //['647173 2c4041', '23293d 4f5a60'],
        //['f5af52 d2ae90', 'd2ae90 ba836f'],
    ];

    function hex_color_to_rgb(out, hex) {
        let sp = 0;
        for (let i = 0; i < 3; ++i) {
            const byte = hex.substring(sp, sp+2);
            const value = parseInt(byte, 16) / 255.;
            out[i] = value;
            sp += 2;
        }
        return out;
    }

    function vec3_pow(out, src, exp) {
        for (let i = 0; i < 3; ++i)
            out[i] = Math.pow(src[i], exp);
        return out;
    }

    const eotf_gamma = 2.2;

    function vec3_linear_from_srgb(out, src) {
        return vec3_pow(out, src, eotf_gamma);
    }

    function vec3_srgb_from_linear(out, src) {
        return vec3_pow(out, src, 1/eotf_gamma);
    }

    function BackgroundColor(r, g, b) {
        this.col_srgb = vec3.fromValues(r, g, b);
        this.col = vec3_linear_from_srgb(vec3.create(), this.col_srgb);
        this.col1 = vec3.clone(this.col);
        this.lerp = uniform(1, 3);
    }

    BackgroundColor.prototype.update = function(env) {
        //const dt = .20 * env.dt * 1e-3;
        const dt = .20 * 1/60;
        vec3.lerp(this.col, this.col, this.col1, dt * this.lerp);
        vec3_srgb_from_linear(this.col_srgb, this.col);
    };

    const background_colors = [
        //new BackgroundColor(0.8, 0.7, 0.7),
        //new BackgroundColor(0.5, 0.5, 0.5),
        new BackgroundColor(0, 0, 0),
        new BackgroundColor(0, 0, 0),
    ];

    let next_background_colors_update_time = 1000;

    function random_gradient() {
        const world = choice(gradient_worlds);
        return choice(world).split(' ').map(c => hex_color_to_rgb([], c));
    }

    function update_background_colors(env) {
        background_colors.forEach(bc => bc.update(env));

        if (env.time < next_background_colors_update_time)
            return;
        next_background_colors_update_time = env.time + expovariate(15000);

        // new gradient
        const [c0, c1] = random_gradient();
        vec3_linear_from_srgb(background_colors[0].col1, c0);
        vec3_linear_from_srgb(background_colors[1].col1, c1);
        background_colors.forEach(bg => bg.lerp = uniform(1, 3));
    }

    function draw_background(env, dither) {
        gl.disable(gl.DEPTH_TEST);
        const pgm = use_program('background');
        if (!pgm) return;

        gl.bindBuffer(gl.ARRAY_BUFFER, fs_buf);
        pgm.vertexAttribPointer('aCoord', 2, gl.FLOAT, false, 0, 0);
        pgm.uniformMatrix4fv('uMatViewProjInv', env.camera.inv_mvp);
        pgm.uniform3fv('uViewPos', env.camera.view_pos);
        pgm.uniform3fv('uBackgroundColor0', background_colors[0].col_srgb);
        pgm.uniform3fv('uBackgroundColor1', background_colors[1].col_srgb);

        {
            const p = env.params.get_value('ambient_light');
            const ambient = (p < 0.001) ? 0 : Math.exp(lerp(-5, 5, 0.5*p));
            //pgm.uniform1f('uBackgroundFade', ambient);
            pgm.uniform1f('uBackgroundFade', 1);
        }
        
        const bg_alpha = 0.5 * (1 + env.params.get_value('dof_clamp_far'));
        pgm.uniform1f('uAlpha', bg_alpha);
        pgm.uniform1i('uDither', dither ? 1 : 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function draw_slogan(env) {
        if (explode_amount < 0.0001)
            return;

        let alpha;
        let z;

        if (exploding) {
            let u = clamp((env.time - explode_start) / 1000, 0, 1);
            alpha = u;
        } else if (explode_amount > 0) {
            let u = clamp((env.time - explode_start) / 300, 0, 1);
            alpha = (1 - u);
        }
        slogan_alpha = alpha;
        z = 1.0*Math.pow(alpha*alpha*(3 - 2*alpha), 0.20);

        const pgm = use_program('slogan');
        if (!pgm) return;

        gl.bindBuffer(gl.ARRAY_BUFFER, buf_quad);
        pgm.vertexAttribPointer('aCoord', 2, gl.FLOAT, false, 0, 0);

        const sc = 1.5;
        mat4.identity(mat);
        mat4.translate(mat, mat, [0, 0, z]);
        mat4.scale(mat, mat, [sc, 0.5*sc, 1]);

        pgm.uniformMatrix4fv('uMatModel', mat);
        pgm.uniformMatrix4fv('uMatViewProj', env.camera.mvp);
        pgm.uniformSampler2D('uTexColor', tex_slogan);
        //pgm.uniform4f('uColor', 0.1, 0.4, 0.5, alpha);
        slogan_color[3] = 0.9 * alpha;
        pgm.uniform4fv('uColor', slogan_color);

        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        gl.disable(gl.BLEND);
    }

    function draw_sculpture(env) {
        {
            dolly += dolly_delta;
            dolly_delta *= 0.9;
            dolly *= (1 - (env.dt * 1e-3));

            const max_dolly = 4;
            const d = dolly - max_dolly;
            if (d > 0) {
                dolly -= 0.2*d;
            }

            env.dolly = dolly;
        }

        update_background_colors(env);

        {
            projectors.forEach((proj, idx) => {
                const slot = env.gallery.slots[idx];
                proj.intensity = 1 * slot.opacity;

                if (slot.seqnum === proj.seqnum)
                    return;

                proj.seqnum = slot.seqnum;

                if (slot.is_video) {
                    // ensure video is front-facing
                    proj.orbit.rotate[0] = 30*DEG2RAD * uniform1();
                    proj.orbit.rotate[1] = 30*DEG2RAD * uniform1();
                    proj.orbit.update();
                    proj.orbit.get_view(proj.camera.view);
                    proj.camera.update();
                } else {
                    proj.randomize();
                }
            });
        }

        if (1) {
            let q = 0.02 + .15*shimmer_params[0];

            if (exploding) {
                q *= 3;
            }

            quat.fromEuler(wobble,
                q * simplex2(0.2, 0.0001*env.time),
                q * simplex2(0.8, -0.0001*env.time),
                0);
            quat.mul(spin, spin, wobble);
            quat.normalize(spin, spin);
        }

        {
            shimmer_params[0] = 5*Math.pow(.5+.5*simplex2(.0001*env.time, .5), 9);
            shimmer_params[1] = lerp(1, 2, .5+.5*simplex2(.0001*env.time, .1));
        }

        quat.mul(rot, rot, spin);
        quat.normalize(rot, rot);

        {
            // damp
            let angle = quat.getAxisAngle(axis, spin);
            angle *= 0.95;
            quat.setAxisAngle(spin, axis, angle);
        }

        if (shaking && !exploding) {
            const t = env.time - shake_start_time;
            const shake_duration = 800;

            mat4.identity(mat_shake);
            const u = Math.min(1, t/shake_duration);
            const amp = .2 * u*u;
            const sc = 1 + amp * simplex2(.1*t, 0.36);
            mat4.scale(mat_shake, mat_shake, [sc, sc, sc]);

            mat4.rotateX(mat_shake, mat_shake, 2*amp * simplex2(0.2*t, 0.89));
            mat4.rotateY(mat_shake, mat_shake, 2*amp * simplex2(0.3*t, 0.49));

            shimmer_params[0] += amp * simplex2(.01*t, 0.5);

            if (!shake_sound_played && (t > shake_duration-200)) {
                env.play_sound('explode', 0.85, 100 * uniform1());
                shake_sound_played = true;
            }

            if (t > shake_duration) {
                exploding = true;
                explode_start = env.time;

                // set slogan color
                vec3.lerp(slogan_color,
                    background_colors[0].col1,
                    background_colors[1].col1,
                    0.5);

                const ambient = env.params.get_value('ambient_light');
                if (((ambient * ambient) * slogan_color[1]) > 0.2) {
                    hex_color_to_rgb(slogan_color, '000000');
                } else {
                    hex_color_to_rgb(slogan_color, 'f3f2e5');
                }
            }
        }
        else if (exploding && !shaking) {
            explode_start = env.time;
            exploding = false;
        }

        const explode_dist = 5;
        if (exploding) {
            let u = clamp((env.time - explode_start) / 300, 0, 1);
            u = u*u*(3- 2*u);
            u = Math.pow(u, 0.25);
            explode_amount = u * explode_dist;
        } else if (explode_amount > 0) {
            let u = clamp((env.time - explode_start) / 1000, 0, 1);
            u = u*u*(3- 2*u);
            u = Math.pow(u, 0.25);
            explode_amount = (1 - u)*explode_dist;
        }

        if (!models) return;
        const model_idx = (explode_amount > 0) ? 0 : 1;
        const model = models.models[model_idx];

        const pgm = use_program('sculpture');
        if (!pgm) return;

        gl.enable(gl.DEPTH_TEST);

        pgm.uniform1f('uTime', env.time * 1e-3);
        pgm.uniform2f('uResolution', gl.drawingBufferWidth, gl.drawingBufferHeight);

        pgm.uniformSampler2D('uTexProjector', env.gallery.texture);

        pgm.uniformSampler2D('uTexOcclusion', model.tex_occlusion);
        pgm.uniform1f('uOcclusionMin', (explode_amount > 0) ? Math.pow(Math.min(1, explode_amount), 0.2) : 0);

        pgm.uniformMatrix4fv('uMatView', env.camera.view);
        pgm.uniformMatrix4fv('uMatViewInv', env.camera.inv_view);
        pgm.uniformMatrix4fv('uMatViewProj', env.camera.mvp);
        
        pgm.uniform3fv('uLightPos', env.light.pos);
        pgm.uniform3f('uLightColor', 1.0, 0.8, 0.7);
        pgm.uniform3f('uColor', .5, .5, .5);

        emission_factor = lerp(emission_factor, slogan_alpha, 0.01);
        pgm.uniform1f('uEmissionFactor', 1-Math.pow(emission_factor, 0.2));

        pgm.uniform4f('uNoiseParams', 
            0.5 + 0.5*simplex2(env.time * .0003, 0.3),
            0.5 + 0.5*simplex2(env.time * .0002, 0.7),
            0.5 + 0.5*simplex2(env.time * .00002, 0.4),
            0);
        pgm.uniform4fv('uShimmerParams', shimmer_params);

        projector_mode = (env.params.get_value('projection_mode') == 'repeat') ? 1: 0;
        pgm.uniform1i('uProjectorMode', projector_mode);

        {
            projectors.forEach(p => p.update());
            update_projector_params();
            pgm.uniformMatrix4fv('uMatProjector[0]', projector_mats);
            pgm.uniform3fv('uProjectorDir[0]', projector_dirs);
            pgm.uniform1fv('uProjectorIntensity[0]', projector_intensities);
        }

        {
            pgm.uniform1f('uRoughness', env.params.get_value('roughness'));
            pgm.uniform1f('uReflectance', env.params.get_value('reflectance'));
            //pgm.uniform1f('uRoughness', 0.2);
            //pgm.uniform1f('uReflectance', 0.9);
            // pgm.uniform1f('uLightIntensity', 1.0);
        }

        {
            // background gradient
            pgm.uniform3fv('uBackgroundColor0', background_colors[0].col);
            pgm.uniform3fv('uBackgroundColor1', background_colors[1].col);
        }

        {
            const params = env.params;

            // visualizers
            pgm.uniform4f('uVisualizeParams',
                0,
                params.get_value('dof_visualize') ? 1 : 0,
                0,
                0);

            // lighting params
            let ambient, diffuse, emission;

            {
                const p = params.get_value('ambient_light');
                ambient = (p < 0.001) ? 0 : Math.exp(lerp(-5, 5, 0.5*p));
            }

            {
                const p = params.get_value('key_light');
                diffuse = (p < 0.001) ? 0 : Math.exp(lerp(-5, 5, 0.5*p));
            }

            {
                const p = params.get_value('projection_light');
                emission = (p < 0.001) ? 0 : Math.exp(lerp(-5, 5, 0.5*p));
            }

            pgm.uniform4f('uLightingParams',
                ambient,
                diffuse,
                0.0,
                emission);

            // depth of field params
            pgm.uniform4f('uDofParams',
                params.get_value('dof_focus'),
                params.get_value('dof_near'),
                params.get_value('dof_far'),
                params.get_value('dof_clamp_far'));
        }

        {
            const {buffers} = models;
            gl.bindBuffer(gl.ARRAY_BUFFER, buffers.vertex);
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buffers.element);
        }

        mat4.fromQuat(mat_parent, rot);

        if (shaking && !exploding) {
            mat4.mul(mat_parent, mat_parent, mat_shake);
        }

        if (1) {
            // translational drift
            const s = 0.2;
            const a = 0.125;
            mat_parent[12] = a*simplex2(0.8, s*-0.0009*env.time);
            mat_parent[13] = a*simplex2(0.3, s*0.0008*env.time);
            mat_parent[14] = a*simplex2(0.7, s*-0.0013*env.time);
        }


        const {parts_start, parts_count} = model;
        for (let i = 0; i < parts_count; ++i) {
            const part_idx = parts_start + i;
            const part = models.parts[part_idx];

            mat4.copy(mat, mat_parent);

            if (explode_amount > 0) {
                const m = part.matrix;
                const a = 1 + explode_amount;
                vec3.set(v0, a*m[12], a*m[13], a*m[14]);
                mat4.translate(mat, mat, v0);
                mat4.fromRotation(part.rot, part.angle, part.axis);
                mat4.mul(mat, mat, part.rot);

                if (exploding) {
                    part.angle += (env.dt*1e-3) * part.spin;
                } else {
                    const a = part.angle / PI2;
                    const a2 = PI2 * (a - Math.floor(a));
                    part.angle = a2 * 0.90;
                }
            } else {
                //mat4.mul(mat, mat, part.matrix);
            }

            mat3.normalFromMat4(mat_normal, mat);

            pgm.uniformMatrix4fv('uMatModel', mat);
            pgm.uniformMatrix3fv('uMatNormal', mat_normal);

            const vs = 32;
            const vo = vs * part.vertex_start;
            pgm.vertexAttribPointer('aPosition', 3, gl.FLOAT, false, vs, vo +  0);
            pgm.vertexAttribPointer('aNormal',   3, gl.FLOAT, false, vs, vo + 12);
            pgm.vertexAttribPointer('aTexCoord', 2, gl.FLOAT, false, vs, vo + 24);
            
            const eo = 2 * part.element_start;
            const ec = part.element_count;
            gl.drawElements(gl.TRIANGLES, ec, gl.UNSIGNED_SHORT, eo);
        }
    }

    function draw(env) {
        update_drag_shaking();

        if (env.params.get_value('dof_enable')) {
            postproc.draw(env, function() {
                draw_background(env, true);
                draw_sculpture(env);
            });

            if (explode_amount > 0.0001) {
                postproc.draw_layer(env, draw_slogan);
            }
        } else {
            draw_background(env, true);
            draw_sculpture(env);
            draw_slogan(env);
        }
    }

    const prev = vec3.create();
    const curr = vec3.create();
    const dir = vec3.create();
    const mat_axis = mat3.create();

    let dragging = false;
    let drag_start = vec2.create();
    let drag_current = vec2.create();
    let drag_start_time = 0;

    function get_window_pos(out, e) {
        if (e.touches && e.touches.length)
            e = e.touches[0];

        const u = e.clientX / (env.canvas.clientWidth - 1);
        const v = 1 - (e.clientY / (env.canvas.clientHeight - 1));

        // to window coords
        out[0] = gl.drawingBufferWidth * u;
        out[1] = gl.drawingBufferHeight * v;
        return out;
    }

    function calc_ray(pos) {
        env.pickray.fromWindowCoords(pos[0], pos[1]);
        return env.pickray;
    }

    function update_drag_shaking() {
        if (!(shaking || exploding)) {
            if (!dragging)
                return;

            const t = env.time - drag_start_time;
            if (t < 50)
                return;

            const d = vec2.dist(drag_current, drag_start);
            if (d > 3)
                return;

            start_shaking();
        } else {
            if (!dragging)
                stop_shaking();
        }
    }

    // TODO touch events

    function on_drag_start(e) {
        // this is for the click to explode
        get_window_pos(drag_start, e);
        vec2.copy(drag_current, drag_start);
        drag_start_time = env.time;

        const ray = calc_ray(drag_current);
        vec3.copy(prev, ray.origin);
        vec3.normalize(dir, ray.direction);
        mat3.normalFromMat4(mat_axis, mat_parent);
        mat3.transpose(mat_axis, mat_axis);

        if (e.type == 'touchstart') {
            document.addEventListener('touchmove', on_drag_move);
            document.addEventListener('touchend', on_drag_end);
        } else {
            document.addEventListener('mousemove', on_drag_move);
            document.addEventListener('mouseup', on_drag_end);
        }

        dragging = true;
        e.preventDefault();
    }

    function on_drag_move(e) {
        get_window_pos(drag_current, e);
        const ray = calc_ray(drag_current);
        vec3.copy(curr, ray.origin);
        vec3.sub(axis, curr, prev);
        const d = vec3.length(axis);
        vec3.normalize(axis, axis);
        vec3.cross(axis, dir, axis);

        mat3.normalFromMat4(mat_normal, mat_parent);
        vec3.transformMat3(axis, axis, mat_axis);

        quat.setAxisAngle(spin, axis, -5*d);
    }

    function on_drag_end(e) {
        document.removeEventListener('mousemove', on_drag_move);
        document.removeEventListener('mouseup', on_drag_end);
        dragging = false;
    }

    env.canvas.addEventListener('mousedown', on_drag_start);
    env.canvas.addEventListener('touchstart', on_drag_start);
    env.canvas.addEventListener('mouseleave', on_drag_end);

    document.onmousewheel = e => {
        dolly_delta -= e.deltaY * .001;
        // e.preventDefault();
    };

    const box = {
        verts: null,
        elems: null,
        elem_count: 0,
    };

    let buf_proj;
    {
        const verts = [];
        for (let i = 0; i < 4; ++i) {
            const u = ((i&1)>>0);
            const v = ((i&2)>>1);
            const x = 2*u - 1;
            const y = 2*v - 1;
            const z = -1;
            verts.push(x, y, z, u, v);
        }
        buf_proj = create_buffer(gl.ARRAY_BUFFER, verts);
    }

    {
        const v = [];
        for (let i = 0; i < 8; ++i) {
            const x = 2*((i&1)>>0) - 1;
            const y = 2*((i&2)>>1) - 1;
            const z = 2*((i&4)>>2) - 1;
            v.push(x, y, z);
        }
        const i = [
            0,1,1,3,3,2,2,0,
            0,4,1,5,3,7,2,6,
            4,5,5,7,7,6,6,4,
        ];
        box.verts = create_buffer(gl.ARRAY_BUFFER, v);
        box.elems = create_buffer(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(i));
        box.elem_count = i.length;
    }

    const mat2 = mat4.create();
    const color = vec4.create();

    function draw_debug(env) {
        if (1) {
            const pgm = use_program('simpletex');
            if (!pgm) return;

            // draw projection image on near plane
            pgm.uniform4f('uColor', 1, 1, 1, 1);
            pgm.uniform1i('uUseTexture', 1);
            pgm.uniformSampler2D('uTexImage', tex_projector);
            pgm.uniformMatrix4fv('uMatViewProj', env.camera.mvp);
            gl.bindBuffer(gl.ARRAY_BUFFER, buf_proj);
            pgm.vertexAttribPointer('aPosition', 3, gl.FLOAT, false, 20, 0);
            pgm.vertexAttribPointer('aTexCoord', 2, gl.FLOAT, false, 20, 12);

            // mat4.identity(mat2);
            // mat4.scale(mat2, mat2, [5,5,5]);

            projectors.forEach(p => {
                pgm.uniformMatrix4fv('uMatModel', p.camera.inv_mvp);
                // pgm.uniformMatrix4fv('uMatModel', mat2);
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            });
        }

        {
            const pgm = use_program('simpletex');
            if (!pgm) return;

            // draw projection frustums
            pgm.uniform1i('uUseTexture', 0);
            pgm.uniformMatrix4fv('uMatViewProj', env.camera.mvp);
            gl.bindBuffer(gl.ARRAY_BUFFER, box.verts);
            pgm.vertexAttribPointer('aPosition', 3, gl.FLOAT, false, 0, 0);
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, box.elems);

            // now draw the projector frustums
            projectors.forEach(p => {
                vec3.copy(color, p.color);
                color[3] = 1;
                pgm.uniform4fv('uColor', color);
                pgm.uniformMatrix4fv('uMatModel', p.camera.inv_mvp);
                gl.drawElements(gl.LINES, box.elem_count, gl.UNSIGNED_SHORT, 0);
            });
        }
    }



    return {
        draw,
        draw_debug,
        draw_background,
        randomize_spin,
    };
}
